/**
 * Cascading two-step picker for Joules checklist-item mapping targets.
 *
 * Step 1: pick the checklist (template). Skip lives here as a top-level option.
 * Step 2: pick the item within that checklist.
 *
 * Mirrors SCH-2.2's StageMappingTab cascading pattern (Pipeline → Stage)
 * including sticky-last-chosen-template in localStorage so admins don't
 * have to re-pick the same checklist for every field they map.
 *
 * Contract carried over from the SCH-2.1 single-dropdown design:
 *   - `value: number | null` — current chosen item id; null = unset OR
 *     explicitly skipped (the parent treats them the same).
 *   - `onChange(id | null)` — fires only on EXPLICIT user actions:
 *     picking a real item id, OR picking the Skip option in the checklist
 *     dropdown. Switching templates without picking a new item does NOT
 *     fire onChange — the row stays in its prior state until a new item
 *     is committed (matches SCH-2.2's form-level canSave gating).
 *
 * Internal disambiguation: `value === null` is overloaded by the parent
 * API (could be "haven't decided yet" OR "explicitly skipped"). The
 * picker tracks `isSkipMode` locally — set true when the user picks
 * Skip, false otherwise. Without it, an unconfirmed discovery row would
 * pre-render as if Skip had been chosen.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTenant } from '@/contexts/TenantContext';
import { SearchableSelect } from '@/components/ui/searchable-select';
import type { JoulesChecklistCandidate } from '@/types/integrations/fieldMapping';

const SKIP_VALUE = '__skip__';
const NO_TEMPLATE_VALUE = '__no_template__';

const STICKY_TEMPLATE_KEY = 'sch-field-mapping-template';

function loadLastTemplateId(tenantId: string | undefined): number | null {
  if (!tenantId || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(`${STICKY_TEMPLATE_KEY}:${tenantId}`);
    if (raw === null) return null;
    if (raw === NO_TEMPLATE_VALUE) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function saveLastTemplateId(
  tenantId: string | undefined,
  templateId: number | null,
): void {
  if (!tenantId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      `${STICKY_TEMPLATE_KEY}:${tenantId}`,
      templateId === null ? NO_TEMPLATE_VALUE : String(templateId),
    );
  } catch {
    // localStorage can be disabled (Safari private browsing) — fail silently.
  }
}

interface TemplateMeta {
  id: number | null; // null = synthetic "(no checklist)" group for orphans
  name: string;
}

function deriveTemplates(candidates: JoulesChecklistCandidate[]): TemplateMeta[] {
  const seenIds = new Set<number>();
  const templates: TemplateMeta[] = [];
  let hasOrphans = false;

  for (const c of candidates) {
    if (c.templateId === null) {
      hasOrphans = true;
      continue;
    }
    if (!seenIds.has(c.templateId)) {
      seenIds.add(c.templateId);
      templates.push({
        id: c.templateId,
        name: c.templateName ?? `(checklist #${c.templateId})`,
      });
    }
  }

  templates.sort((a, b) => a.name.localeCompare(b.name));

  if (hasOrphans) {
    templates.push({ id: null, name: '(no checklist)' });
  }
  return templates;
}

interface JoulesTargetSelectorProps {
  candidates: JoulesChecklistCandidate[];
  value: number | null;
  onChange: (value: number | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function JoulesTargetSelector({
  candidates,
  value,
  onChange,
  placeholder = 'Choose a Joules checklist item',
  disabled,
}: JoulesTargetSelectorProps) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.tenant_id;

  const templates = useMemo(() => deriveTemplates(candidates), [candidates]);

  const selectedItem = candidates.find((c) => c.id === value) ?? null;

  // Initial template: parent of selected item > sticky > first template.
  const initialTemplateId = (() => {
    if (selectedItem) return selectedItem.templateId;
    const sticky = loadLastTemplateId(tenantId);
    if (sticky !== null && templates.some((t) => t.id === sticky)) return sticky;
    return templates[0]?.id ?? null;
  })();

  const [templateId, setTemplateId] = useState<number | null>(initialTemplateId);
  // Track explicit Skip choice locally — `value === null` alone is ambiguous.
  const [isSkipMode, setIsSkipMode] = useState(false);

  // Sync internal state when the parent's value changes externally:
  //   - Real item: clear skip mode + lock templateId to the item's parent.
  //   - Null: ambiguous, leave skip mode as-is (the user's last action wins).
  useEffect(() => {
    if (selectedItem) {
      setIsSkipMode(false);
      if (selectedItem.templateId !== templateId) {
        setTemplateId(selectedItem.templateId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const itemsInTemplate = useMemo(
    () =>
      candidates
        .filter((c) => c.templateId === templateId)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [candidates, templateId],
  );

  // ─── Template dropdown ────────────────────────────────────────────────
  const templateOptions = [
    { value: SKIP_VALUE, label: 'Skip — do not import this field' },
    ...templates.map((t) => ({
      value: t.id === null ? NO_TEMPLATE_VALUE : String(t.id),
      label: t.name,
    })),
  ];

  const templateDropdownValue = isSkipMode
    ? SKIP_VALUE
    : templateId === null
      ? NO_TEMPLATE_VALUE
      : String(templateId);

  function handleTemplateChange(v: string) {
    if (v === SKIP_VALUE) {
      setIsSkipMode(true);
      onChange(null);
      return;
    }
    setIsSkipMode(false);
    const next = v === NO_TEMPLATE_VALUE ? null : Number(v);
    setTemplateId(next);
    saveLastTemplateId(tenantId, next);
    // Switching template does NOT fire onChange — the parent's value stays
    // as-is until the user explicitly picks a new item from the second
    // dropdown. This matches SCH-2.2's pipeline-change behaviour (see
    // StageMappingTab `handlePipelineChange`).
  }

  // ─── Item dropdown ────────────────────────────────────────────────────
  const itemOptions = itemsInTemplate.map((c) => ({
    label: c.label || `(unnamed item #${c.id})`,
    value: String(c.id),
    description: c.itemType,
  }));

  const itemDropdownValue =
    selectedItem && selectedItem.templateId === templateId ? String(value) : '';

  function handleItemChange(v: string) {
    if (!v) return;
    const parsed = Number(v);
    if (Number.isFinite(parsed)) onChange(parsed);
  }

  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
      <div className="flex-1 min-w-0">
        <SearchableSelect
          options={templateOptions}
          value={templateDropdownValue}
          onValueChange={handleTemplateChange}
          placeholder="Choose a checklist"
          disabled={disabled}
          inDialog
        />
      </div>
      {!isSkipMode && (
        <div className="flex-1 min-w-0">
          <SearchableSelect
            options={itemOptions}
            value={itemDropdownValue}
            onValueChange={handleItemChange}
            placeholder={
              itemsInTemplate.length === 0
                ? '(no items in this checklist)'
                : placeholder
            }
            disabled={disabled || itemsInTemplate.length === 0}
            inDialog
          />
        </div>
      )}
    </div>
  );
}
